/**
 * One reading of a download reply, for every download on the Study Documents
 * page (Jim's plan, 2026-10-25).
 *
 * The page used to have five download patterns: a signed URL, base64 Word in
 * three differently named fields (`docx`, `docxBase64`, `base64`), CSV text
 * inline, a soft `{blocked}` refusal from the verification gate, and a
 * `verification` status riding on composite downloads. This module turns any
 * of those replies into one of a few outcomes, and builds every message the
 * user sees about a download, using the document's plain name.
 *
 * Pure and React-free: tested under the functions package's vitest
 * (functions/src/downloads/downloadStandardWeb.test.ts).
 *
 * Copy rules: plain language, the document's own name, never an internal
 * callable or check name, never "AI".
 */
import { gateRefusalMessage, type DdDownloadResponseLike } from '../verification/ddGateMessages';

export const WORD_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
export const CSV_MIME = 'text/csv;charset=utf-8';

/** The fields any download callable on the page replies with. */
export interface DownloadReplyLike extends DdDownloadResponseLike {
  downloadUrl?: string;
  filename?: string;
  mimeType?: string;
  /** SUPPQUAL templates: 0 when the mapping has no SUPP-- domains. */
  fileCount?: number;
  csv?: string;
  /** DMP and Edit Check Specification Word exports. */
  docx?: string;
  /** Legacy CRF Instruction Guide Word export. */
  docxBase64?: string;
  /** EDTS, Medical Coding, DRCP, SAE Reconciliation, Database Lock Word exports. */
  base64?: string;
}

export type NormalisedDownload =
  | { kind: 'blocked'; message: string }
  | { kind: 'url'; url: string; filename: string; mimeType?: string; warning: string | null }
  | { kind: 'base64'; base64: string; filename: string; mimeType: string; warning: string | null }
  | { kind: 'text'; text: string; filename: string; mimeType: string; warning: string | null }
  /** The server said there is nothing to download (fileCount 0). */
  | { kind: 'nothing'; warning: string | null }
  /** No file in the reply; the caller may know an older shape (legacy guide markdown). */
  | { kind: 'unrecognised'; warning: string | null };

export interface NormaliseOptions {
  /** Plain document name, e.g. "Data Management Plan". */
  docName: string;
  /** Used when the server names no file. */
  fallbackFilename: string;
  /** Wins over the server's filename (the guide builds its name client-side). */
  filenameOverride?: string;
  /**
   * The refusal line for a soft `{blocked}` reply. Defaults to the generic gate
   * line with the plain name; the Data Dictionary and Edit Check Specification
   * pass their existing messages so their wording is unchanged.
   */
  refusal?: (data: DdDownloadResponseLike) => string | null;
  /** MIME type for inline text replies. */
  textMime?: string;
}

const nonEmpty = (v: unknown): v is string => typeof v === 'string' && v.trim().length > 0;

/** Verification statuses that need no warning. */
const CLEAN_STATUSES = new Set(['clean', 'verified']);
/** Statuses meaning "not checked", as opposed to "checked and found differences". */
const UNCHECKED_STATUSES = new Set(['unknown', 'unverified', 'stale', 'stale-or-missing']);

/**
 * The warning shown after a download whose reply carries a verification status
 * that is not clean. Generalises the SDTM panel's earlier line ("This download
 * is available, but its last verification found differences") to every
 * document, by its plain name. null when there is nothing to warn about.
 */
export function verificationWarningMessage(
  docName: string,
  verification: { status?: string } | null | undefined,
): string | null {
  const kind = verificationWarningKind(verification?.status);
  if (kind === 'unverified') {
    return `The ${docName} downloaded, but it has not been verified since it last changed. See the Verification page.`;
  }
  if (kind === 'differences') {
    return `The ${docName} downloaded, but its last verification found differences. See the Verification page.`;
  }
  return null;
}

/**
 * THE RULE for whether a download warns about verification, and which way.
 * Every download on the page goes through this one function.
 *
 * Jim, 2026-10-25: warn only when Verification found differences. "Not
 * verified yet" (unknown / unverified / stale) says nothing: every study that
 * has not run Verification would otherwise warn on every download, and a
 * transient failure to read the status reads as "unknown" too. Return
 * 'unverified' here to bring that warning back; its wording is kept above.
 */
export function verificationWarningKind(status: string | undefined): 'differences' | 'unverified' | null {
  if (!nonEmpty(status) || CLEAN_STATUSES.has(status)) return null;
  if (UNCHECKED_STATUSES.has(status)) return null;
  return 'differences';
}

export function downloadSuccessMessage(docName: string): string {
  return `${docName} downloaded`;
}

/** Strips SDK prefixes so the server's own sentence is what remains. */
function serverText(err: unknown): string {
  const message = (err as { message?: unknown } | null)?.message;
  if (typeof message !== 'string') return '';
  return message
    .replace(/^FirebaseError:\s*/i, '')
    .trim();
}

function errorCode(err: unknown): string {
  const code = (err as { code?: unknown } | null)?.code;
  return typeof code === 'string' ? code.replace(/^functions\//, '') : '';
}

/**
 * The one error line for a failed download.
 *
 * A failed-precondition refusal is the server telling the user what to fix
 * ("This document cannot be downloaded while a check is failing: …"), so its
 * message is passed through unchanged. Anything else reads
 * "Could not download the <plain name>: <reason>".
 */
export function downloadErrorMessage(docName: string, err: unknown): string {
  const code = errorCode(err);
  const text = serverText(err);
  if (code === 'failed-precondition' && text) return text;
  let reason: string;
  if (code === 'deadline-exceeded') {
    reason = 'it took too long to prepare. Please try again.';
  } else if (!text || /^internal$/i.test(text) || /^deadline-exceeded$/i.test(text)) {
    reason = 'something went wrong while preparing it. Please try again.';
  } else {
    reason = text;
  }
  return `Could not download the ${docName}: ${reason}`;
}

/** Reads a download reply. Never throws; an empty reply is 'unrecognised'. */
export function normaliseDownloadReply(data: unknown, opts: NormaliseOptions): NormalisedDownload {
  const d = (data && typeof data === 'object' ? data : {}) as DownloadReplyLike;

  if (d.blocked) {
    const refusal = opts.refusal ?? ((x: DdDownloadResponseLike) => gateRefusalMessage(opts.docName, x));
    return {
      kind: 'blocked',
      message: refusal(d) ?? `The ${opts.docName} cannot be downloaded yet. See the Verification page.`,
    };
  }

  const warning = verificationWarningMessage(opts.docName, d.verification);
  const filename = nonEmpty(opts.filenameOverride)
    ? opts.filenameOverride
    : nonEmpty(d.filename)
    ? d.filename
    : opts.fallbackFilename;

  if (d.fileCount === 0) return { kind: 'nothing', warning };

  if (nonEmpty(d.downloadUrl)) {
    return {
      kind: 'url',
      url: d.downloadUrl,
      filename,
      ...(nonEmpty(d.mimeType) ? { mimeType: d.mimeType } : {}),
      warning,
    };
  }

  const b64 = [d.docx, d.docxBase64, d.base64].find(nonEmpty);
  if (b64) {
    return { kind: 'base64', base64: b64, filename, mimeType: nonEmpty(d.mimeType) ? d.mimeType : WORD_MIME, warning };
  }

  if (typeof d.csv === 'string') {
    return { kind: 'text', text: d.csv, filename, mimeType: opts.textMime ?? CSV_MIME, warning };
  }

  return { kind: 'unrecognised', warning };
}

/** Decodes base64 to bytes for a Blob. */
export function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

/** Elapsed seconds as mm:ss. */
export function formatElapsed(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(Number.isFinite(totalSeconds) ? totalSeconds : 0));
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

export const SLOW_DOWNLOAD_LINE = 'This can take a few minutes';
export const PREPARING_LABEL = 'Preparing…';

export interface DownloadButtonStateInput {
  /** The button's resting label, e.g. "Download". */
  label: string;
  /** Request ids this button can start. */
  optionIds: readonly string[];
  /** The id of the download running on this card, if any. */
  runningId: string | null;
  /** Whether the running request is a slow one. */
  runningSlow: boolean;
  /** Seconds since the running download started. */
  elapsedSeconds: number;
  /** Disabled for another reason (no document yet). */
  disabled?: boolean;
}

export interface DownloadButtonState {
  label: string;
  disabled: boolean;
  spinning: boolean;
  /** "This can take a few minutes · 01:05", or null. */
  slowLine: string | null;
}

/**
 * What a download button shows. While any download on the card runs, every
 * download button on it is disabled; the one whose download is running reads
 * "Preparing…" with a spinner, and a slow one also shows the elapsed time.
 */
export function downloadButtonState(input: DownloadButtonStateInput): DownloadButtonState {
  const mine = input.runningId !== null && input.optionIds.includes(input.runningId);
  return {
    label: mine ? PREPARING_LABEL : input.label,
    disabled: input.runningId !== null || !!input.disabled,
    spinning: mine,
    slowLine: mine && input.runningSlow ? `${SLOW_DOWNLOAD_LINE} · ${formatElapsed(input.elapsedSeconds)}` : null,
  };
}
